import React, { useMemo } from 'react';
import { Button } from '@veneer/core';
import { useFeatureFlagContext } from '@eu-data-act/react-euda-commons/dist/index';
import { useDependencyManagerContext } from '../../../contexts/dependencyManager';
import { ScreenReaderOnly } from '../styled';

type TelemetryRequestButtonProps = {
  'data-testid': string;
  deviceFamily: string;
  disabled: boolean;
  loading: boolean;
  serialNumber: string;
  handleOnClick: () => void;
  tabIndex?: number;
};

const TelemetryRequestButton: React.FC<TelemetryRequestButtonProps> = ({
  'data-testid': dataTestId,
  disabled,
  handleOnClick,
  loading,
  deviceFamily,
  serialNumber,
  tabIndex
}) => {
  const { translate } = useDependencyManagerContext();
  const releasePlatformHpAnalytics = useFeatureFlagContext();

  const buttonTextKey = disabled
    ? 'components.device-item.telemetry-request-button.data-requested'
    : 'components.device-item.telemetry-request-button.request-data';

  const ariaLabelKey = disabled
    ? 'components.device-item.telemetry-request-button.aria-label-requested'
    : 'components.device-item.telemetry-request-button.aria-label-request';

  const ariaDescriptionKey = disabled
    ? 'components.device-item.telemetry-request-button.aria-description-requested'
    : 'components.device-item.telemetry-request-button.aria-description-request';

  const unknownDeviceName = translate('components.device-item.no-data.unknown');

  const requestButtonAnalyticsAttributes = useMemo(
    () =>
      releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'linkClick',
            'data-gtm-id': `device-card-request-button-${serialNumber}`,
            'data-gtm-value': `request-data-${deviceFamily}-${serialNumber}`
          }
        : {},
    [serialNumber, deviceFamily, releasePlatformHpAnalytics]
  );

  return (
    <Button
      disabled={disabled}
      appearance={disabled ? 'primary' : 'secondary'}
      data-testid={dataTestId}
      onClick={handleOnClick}
      loading={loading}
      tabIndex={tabIndex}
      aria-label={translate(ariaLabelKey, {
        deviceFamily: deviceFamily || unknownDeviceName,
        serialNumber
      })}
      aria-describedby={`${dataTestId}-description`}
      {...requestButtonAnalyticsAttributes}
    >
      {translate(buttonTextKey, {
        deviceFamily: deviceFamily || unknownDeviceName,
        serialNumber
      })}
      <ScreenReaderOnly
        id={`${dataTestId}-description`}
        aria-live="polite"
      >
        {translate(ariaDescriptionKey, {
          deviceFamily: deviceFamily || unknownDeviceName,
          serialNumber
        })}
      </ScreenReaderOnly>
    </Button>
  );
};

export default TelemetryRequestButton;
